/**
 * Store control ("Do'kon nazorati", docs/plans/store_control_v1.md) client API shapes: aliases of the types generated
 * from the real client API OpenAPI schema (`npm run generate:api-types`), named for the panel. Only query shapes the
 * panel builds itself and runtime lists of enum values are written here.
 */
import { exactList } from './api_constants';
import type { TradingDate } from './api_types';
import type { components as requestComponents } from './schema/generated_request_schema';
import type { components, operations } from './schema/generated_schema';

type Schema<SchemaName extends keyof components['schemas']> = components['schemas'][SchemaName];

export type ControlState = Schema<'StoreControlState'>;

/** TZ section 2/5 states (stable English values, contracts StoreControlState), in display order. */
export const CONTROL_STATES = exactList<ControlState>()([
  'staff_phone',
  'post_empty',
  'customer_unattended',
  'store_no_staff',
  'service_wait',
  'staff_gathering',
]);

/** States decided per store over all its cameras together: no zone, the store is the place. */
export const STORE_LEVEL_CONTROL_STATES: ReadonlySet<ControlState> = new Set(['store_no_staff']);

export function isControlState(candidate: unknown): candidate is ControlState {
  return typeof candidate === 'string' && (CONTROL_STATES as readonly string[]).includes(candidate);
}

/** Store control permission flags of `PermissionFlags` (the API keeps the dotted names). */
export type ControlPermissionKey = 'control.view' | 'control.media' | 'control.flag';

export interface ControlRangeQuery {
  from: TradingDate;
  to: TradingDate;
  /** Absent = every store the user may see. */
  storeIds?: readonly string[] | undefined;
}

export type ControlCompareMode = NonNullable<
  operations['get_control_summary']['parameters']['query']['compare']
>;

export type ControlStateTotals = Schema<'ControlStateTotals'>;
export type ControlWorstStore = Schema<'ControlWorstStore'>;
export type ControlHeatmapCell = Schema<'ControlHeatmapCell'>;
export type ControlZoneTotals = Schema<'ControlZoneTotals'>;
export type ControlOpenCount = Schema<'ControlOpenCount'>;
export type ControlScore = Schema<'ControlScore'>;
/** GET /api/v1/control/summary?from&to&store_ids&compare (served from aggregates only). */
export type ControlSummary = Schema<'ControlSummary'>;
/** GET /api/v1/control/status: whether the company has store control and what this user may do there. */
export type ControlStatus = Schema<'ControlStatus'>;

export type ControlEventItem = Schema<'ClientControlEventItem'>;
/**
 * A store-level event ("no staff in store") has one frame from each of the store's cameras, so a frame names its
 * camera (requested from the center for v1.1; a frame without it belongs to the event's camera).
 */
export type ControlEvidenceFrame = Schema<'ClientEvidenceFrame'> & {
  camera_id?: string | null;
  camera_name?: string | null;
};
/** GET /api/v1/control/events/{id}. */
export type ControlEventDetail = Omit<Schema<'ClientControlEventDetail'>, 'evidence'> & {
  evidence: ControlEvidenceFrame[];
};
/** GET /api/v1/control/events: newest first, cursor pagination. */
export type ControlEventPage = Schema<'ClientControlEventPage'>;
export type ControlFalsePositiveRequest = requestComponents['schemas']['FalsePositiveRequest'];
export type ControlFalsePositiveResult = Schema<'ControlFalsePositiveResult'>;

export interface ControlEventsQuery extends ControlRangeQuery {
  states?: readonly ControlState[] | undefined;
  zoneIds?: readonly string[] | undefined;
  /** Only events open right now (store page block). */
  openOnly?: boolean | undefined;
  cursor?: string | null | undefined;
  limit?: number | undefined;
}

export type ControlCompareResult = Schema<'ControlCompareResult'>;
/** per_1000_visits: events per 1 000 visits; per_work_hour: event minutes per work hour. */
export type ControlCompareMetric = ControlCompareResult['metric'];
export const CONTROL_COMPARE_METRICS = [
  'per_1000_visits',
  'per_work_hour',
] as const satisfies readonly ControlCompareMetric[];
export type ControlCompareRow = Schema<'ControlCompareRow'>;

export interface ControlCompareQuery extends ControlRangeQuery {
  metric: ControlCompareMetric;
  state?: ControlState | null | undefined;
}

export type ServiceWaitStats = Schema<'ServiceWaitStats'>;
export type ServiceHourRow = Schema<'ServiceHourRow'>;
export type ServiceWeekdayRow = Schema<'ServiceWeekdayRow'>;
export type ServiceDayRow = Schema<'ServiceDayRow'>;
export type ServiceStoreRow = Schema<'ServiceStoreRow'>;
/** GET /api/v1/control/service-quality?from&to&store_ids&compare (H4). */
export type ServiceQualityResult = Schema<'ServiceQualityResult'>;

/** Filters of the "control_events" Excel export (`POST /exports`, `params.control`). */
export type ControlExportFilters = requestComponents['schemas']['ControlExportFilters'];
